import { useCallback, useEffect, useState } from 'react';
import { TAutoReplyMessageAsset } from 'api';
import { Text, Button } from '../../../components';
import ContentSection from '../ContentSection';
import {
  TMessageSection,
  TMessageSendOption,
  EMoveDirection,
  EContentSectionType,
  ESendRecipient,
  TSectionPatch,
} from 'types';
import {
  MAX_BULK_MESSAGE_SECTIONS,
  commonStrings,
  sendAllMessageStrings,
} from '../../../constants';
import {
  areMessageAssetSectionsValid,
  createSection,
  hasAnyContent,
} from 'utils/messageSection';
import RecipientSelector from '../RecipientSelector';
import SendTimeSelector from '../SendTimeSelector';

type Props = {
  sections: TMessageSection[];
  sendOption: TMessageSendOption;
  assets: TAutoReplyMessageAsset[];
  assetsReady: boolean;
  onSectionsChange: React.Dispatch<React.SetStateAction<TMessageSection[]>>;
  onSendOptionChange: (option: Partial<TMessageSendOption>) => void;
  onSend: () => void;
  onTestSend: () => void;
  isLoading: boolean;
  isTestLoading: boolean;
  deliveryCount?: number | null;
  isDeliveryCountLoading: boolean;
  isDeliveryCountError: boolean;
  isDeliveryCountReady: boolean;
  onRetryDeliveryCount: () => void;
  testDeliveryCount?: number | null;
  onCanSaveDraftChange: (canSaveDraft: boolean) => void;
  /** 送信ボタンの文言。配信予約の編集では「予約を更新」に差し替える */
  sendLabel?: string;
  /** 配信予約の編集では即時配信へ切り替えさせない */
  disableImmediateSend?: boolean;
};

function BulkMessageEditor({
  sections,
  sendOption,
  assets,
  assetsReady,
  onSectionsChange,
  onSendOptionChange,
  onSend,
  onTestSend,
  isLoading,
  isTestLoading,
  deliveryCount,
  isDeliveryCountLoading,
  isDeliveryCountError,
  isDeliveryCountReady,
  onRetryDeliveryCount,
  testDeliveryCount,
  onCanSaveDraftChange,
  sendLabel,
  disableImmediateSend = false,
}: Props) {
  // 招待画像のアップロード中にsectionを確定させるとimageUrl未設定のまま
  // 送信・下書き保存されるため、進行中のsection idを集約して無効化する
  const [uploadingSurveySectionIds, setUploadingSurveySectionIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const handleSurveyUploadingChange = useCallback(
    (sectionId: string, uploading: boolean) => {
      setUploadingSurveySectionIds((current) => {
        if (current.has(sectionId) === uploading) return current;
        const next = new Set(current);
        if (uploading) {
          next.add(sectionId);
        } else {
          next.delete(sectionId);
        }
        return next;
      });
    },
    [],
  );
  const isSurveyImageUploading = uploadingSurveySectionIds.size > 0;
  const firstTextSectionIndex = sections.findIndex(
    (section) => section.type === EContentSectionType.TEXT,
  );

  // recipient=FILTER なのに対象タグ未選択のときは送信不可（空セグメント送信を防ぐ）
  const isRecipientInvalid =
    sendOption.recipient === ESendRecipient.FILTER &&
    (sendOption.tagIds?.length ?? 0) === 0;
  const hasValidContent =
    hasAnyContent(sections) &&
    areMessageAssetSectionsValid(sections, assets, assetsReady) &&
    !isSurveyImageUploading;
  const isSendDisabled =
    !hasValidContent ||
    isLoading ||
    !isDeliveryCountReady ||
    isRecipientInvalid;

  useEffect(() => {
    onCanSaveDraftChange(hasValidContent);
  }, [hasValidContent, onCanSaveDraftChange]);

  const handleAddSection = useCallback(() => {
    onSectionsChange((prev) => [
      ...prev,
      createSection(EContentSectionType.TEXT),
    ]);
  }, [onSectionsChange]);

  const handlePatchSection = useCallback(
    (id: string, patch: TSectionPatch) => {
      onSectionsChange((prev) =>
        prev.map((section) => {
          if (section.id !== id) return section;

          if (section.type === EContentSectionType.TEXT) {
            if (!('content' in patch)) return section;
            return { ...section, content: patch.content };
          }

          if (!('file' in patch)) return section;

          return { ...section, file: patch.file };
        }),
      );
    },
    [onSectionsChange],
  );

  const handleChangeType = useCallback(
    (id: string, nextType: EContentSectionType) => {
      onSectionsChange((prev) =>
        prev.map((section) =>
          section.id === id ? createSection(nextType) : section,
        ),
      );
    },
    [onSectionsChange],
  );

  const handleDeleteSection = useCallback(
    (id: string) => {
      onSectionsChange((prev) => prev.filter((section) => section.id !== id));
    },
    [onSectionsChange],
  );

  const handleMoveSection = useCallback(
    (id: string, direction: EMoveDirection) => {
      onSectionsChange((prev) => {
        const index = prev.findIndex((s) => s.id === id);
        if (index === -1) return prev;
        const next = [...prev];

        if (direction === EMoveDirection.UP && index > 0) {
          [next[index - 1], next[index]] = [next[index], next[index - 1]];
        }
        if (direction === EMoveDirection.DOWN && index < next.length - 1) {
          [next[index], next[index + 1]] = [next[index + 1], next[index]];
        }

        return next;
      });
    },
    [onSectionsChange],
  );

  const handleSelectFile = useCallback(
    (id: string, file: File) => {
      handlePatchSection(id, { file });
    },
    [handlePatchSection],
  );

  const handleUpdateSection = useCallback(
    (id: string, updates: Partial<TMessageSection>) => {
      onSectionsChange((prev) =>
        prev.map((section) => {
          if (section.id !== id) return section;
          // Ensure type safety by preserving the section type
          const updated = { ...section, ...updates };
          // Remove undefined values to maintain type safety
          Object.keys(updated).forEach((key) => {
            if (updated[key as keyof TMessageSection] === undefined) {
              delete updated[key as keyof TMessageSection];
            }
          });
          return updated as TMessageSection;
        }),
      );
    },
    [onSectionsChange],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <Text className="py-3 flex justify-between items-center border-b border-gray-200" bold>
          {sendAllMessageStrings.content}
        </Text>

        <RecipientSelector
          sendOption={sendOption}
          onSendOptionChange={onSendOptionChange}
          className="mb-2"
        />

        <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
          <SendTimeSelector
            sendOption={sendOption}
            onSendOptionChange={onSendOptionChange}
            className="mb-4"
            disableImmediate={disableImmediateSend}
          />

          <div className="sm:pl-4 pt-4">
            {isDeliveryCountLoading ? (
              <Text className="py-2" bold>
                {commonStrings.loading}
              </Text>
            ) : isDeliveryCountError ? (
              <div className="flex items-center gap-2 py-2">
                <Text className="text-red-500" bold>
                  {sendAllMessageStrings.deliveryCountLoadFailed}
                </Text>
                <button
                  type="button"
                  className="text-xs font-semibold text-primary underline"
                  onClick={onRetryDeliveryCount}
                >
                  {sendAllMessageStrings.deliveryCountRetry}
                </button>
              </div>
            ) : deliveryCount === 0 ? (
              <Text className="py-2 text-red-500" bold>
                {sendAllMessageStrings.noDeliverableUsers}
              </Text>
            ) : (
              <Text className="py-2" bold>
                {deliveryCount ? sendAllMessageStrings.numberOfDeliveriesMessage.replace('${count}',
                  deliveryCount.toLocaleString('ja-JP')) : ''}
              </Text>
            )}
          </div>
        </div>


        <div className="flex flex-col gap-4">
          {sections.map((section, index) => (
            <ContentSection
              key={section.id}
              section={section}
              index={index}
              totalSections={sections.length}
              showNameInsertionHints={index === firstTextSectionIndex}
              assets={assets}
              onChangeType={(type) => handleChangeType(section.id, type)}
              onUpdateSection={(updates) => handleUpdateSection(section.id, updates)}
              onChangeContent={(content) =>
                handlePatchSection(section.id, { content })
              }
              onChangeFile={(file) => handleSelectFile(section.id, file)}
              onRemoveFile={() =>
                handlePatchSection(section.id, { file: null })
              }
              onDelete={() => handleDeleteSection(section.id)}
              onMove={(direction) => handleMoveSection(section.id, direction)}
              onSurveyUploadingChange={(uploading) =>
                handleSurveyUploadingChange(section.id, uploading)
              }
            />
          ))}
        </div>

        {sections.length < MAX_BULK_MESSAGE_SECTIONS && (
          <div className="pt-4 mt-2">
            <button
              onClick={handleAddSection}
              className="px-4 py-2 border border-primary rounded-lg hover:bg-primary-50 text-primary text-sm"
            >
              {sendAllMessageStrings.addContent}
            </button>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 px-4 lg:-translate-y-[53px] ml-auto w-fit">
        <Button
          variant="outline"
          onClick={onTestSend}
          disabled={
            !hasValidContent ||
            isLoading ||
            isTestLoading ||
            testDeliveryCount === 0 ||
            // 本配信の対象が0人（「配信できるユーザーがいません」表示中）は
            // テスト配信しても本配信できないため合わせて無効化する
            deliveryCount === 0
          }
          loading={isTestLoading}
          className="px-6 py-2 rounded-lg disabled:opacity-50"
        >
          {sendAllMessageStrings.testSend.button}
        </Button>
        <Button
          onClick={onSend}
          disabled={isSendDisabled || isTestLoading}
          loading={isLoading}
          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
        >
          {sendLabel ?? commonStrings.send}
        </Button>
      </div>
    </div>
  );
}

export default BulkMessageEditor;
